import { useState, useMemo, useCallback } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useWithdrawFromPayoutAccountMutation } from '@graphql/mutations/registry.graphql.generated';
import { RegistryPayoutAccountExternalAccountPayoutMethod } from '@graphql/generated-base';
import type { PayoutAccountInfo } from '../CreditCardSetUp.context';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import { MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS } from '@shared/components/PayoutDialog/payoutFeeUtils';
import type { PayoutStatus, PayoutDialogStep } from '@shared/components/PayoutDialog/PayoutDialog.types';
import { useFeatureValue } from '@shared/core/featureFlags';
import { getTransferPartnerConfig } from '@shared/components/TransferToBankDialog/transferPartnerConfigs';

export const usePayoutDialogController = (fetchPayoutInfo: () => void, payoutAccountInfo: PayoutAccountInfo | null, eventId: string) => {
  const telemetry = useCreditCardSetupTelemetry();
  const [status, setStatus] = useState<PayoutStatus>('pending');
  const isReadyToUse = payoutAccountInfo?.status === 'readyToUse';

  const availableInMinorUnits = payoutAccountInfo?.balance?.available.valueInMinorUnits ?? 0;
  const isPayoutAllowed = availableInMinorUnits > MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS;

  const { value: activePartner } = useFeatureValue('activeCashFundPartner');
  const { value: transferPartner } = useFeatureValue('enableCashFundPartnerTransferToBank');
  const { value: repeatTransferPartner } = useFeatureValue('enableCashFundPartnerTransferToBankRepeat');
  const hasActiveInitialPromo = Boolean(!isReadyToUse && activePartner && activePartner === transferPartner && getTransferPartnerConfig(activePartner));
  const hasActiveRepeatPromo = Boolean(isReadyToUse && isPayoutAllowed && activePartner && activePartner === repeatTransferPartner && getTransferPartnerConfig(activePartner));
  const hasActivePromo = hasActiveInitialPromo || hasActiveRepeatPromo;

  const [dialogStep, setDialogStep] = useState<PayoutDialogStep>(() => (!isReadyToUse || hasActivePromo ? 'connectBank' : 'amount'));
  const [selectedMethod, setSelectedMethod] = useState<RegistryPayoutAccountExternalAccountPayoutMethod>(RegistryPayoutAccountExternalAccountPayoutMethod.standard);

  const maxAmountInMinorUnits = useMemo(() => {
    if (payoutAccountInfo?.payoutLimitInMinorUnits != null) {
      return Math.min(payoutAccountInfo.payoutLimitInMinorUnits, availableInMinorUnits);
    }
    return availableInMinorUnits;
  }, [payoutAccountInfo?.payoutLimitInMinorUnits, availableInMinorUnits]);

  const [amountInMinorUnits, setAmountInMinorUnits] = useState<number>(() => maxAmountInMinorUnits);

  const payoutAccountAllowed = useMemo(
    () => Boolean(payoutAccountInfo?.balance && payoutAccountInfo.balance.available.valueInMinorUnits > MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS),
    [payoutAccountInfo],
  );

  const { isOpen: isOpenPayoutDialog, onOpen: onOpenPayoutDialog, onClose } = useDisclosure();

  const [withdrawBalance, { loading: loadingPayoutRequest }] = useWithdrawFromPayoutAccountMutation({
    variables: {
      eventId,
      payoutMethod: selectedMethod,
      amountInMinorUnits,
    },
    onCompleted: response => {
      const withdrawal = response.withdrawFromPayoutAccount;
      if (withdrawal.status !== 'failed') {
        telemetry.payoutDialog.enter(withdrawal.status);
        fetchPayoutInfo();
        setStatus('success');
        setDialogStep('confirmation');
      } else {
        telemetry.payoutDialog.enter(withdrawal.status);
        setStatus('error');
        setDialogStep('confirmation');
      }
    },
    onError: () => {
      telemetry.payoutDialog.enter('error');
      setStatus('error');
      setDialogStep('confirmation');
    },
  });

  const handleTransfer = useCallback(() => {
    withdrawBalance();
  }, [withdrawBalance]);

  const onContinueToMethod = useCallback(() => {
    setDialogStep('method');
  }, []);

  const onContinueToAmount = useCallback(() => {
    setDialogStep('amount');
  }, []);

  const onBackToAmount = useCallback(() => {
    setDialogStep('amount');
  }, []);

  const onCloseDialog = useCallback(() => {
    setStatus('pending');
    setDialogStep(!isReadyToUse || hasActivePromo ? 'connectBank' : 'amount');
    setSelectedMethod(RegistryPayoutAccountExternalAccountPayoutMethod.standard);
    setAmountInMinorUnits(maxAmountInMinorUnits);
    onClose();
  }, [maxAmountInMinorUnits, onClose, isReadyToUse, hasActivePromo]);

  const onOpenDialog = useCallback(() => {
    const newMax = payoutAccountInfo?.payoutLimitInMinorUnits != null ? Math.min(payoutAccountInfo.payoutLimitInMinorUnits, availableInMinorUnits) : availableInMinorUnits;
    setAmountInMinorUnits(newMax);
    setDialogStep(!isReadyToUse || hasActivePromo ? 'connectBank' : 'amount');
    setStatus('pending');
    setSelectedMethod(RegistryPayoutAccountExternalAccountPayoutMethod.standard);
    onOpenPayoutDialog();
  }, [payoutAccountInfo, availableInMinorUnits, onOpenPayoutDialog, isReadyToUse, hasActivePromo]);

  const resetPayoutStatus = useCallback(() => {
    setStatus('pending');
  }, []);

  return {
    handleTransfer,
    loadingPayoutRequest,
    payoutAccountAllowed,
    isOpenPayoutDialog,
    onOpenPayoutDialog: onOpenDialog,
    onClosePayoutDialog: onCloseDialog,
    resetPayoutStatus,
    payoutStatus: status,
    accountNumber: payoutAccountInfo?.externalAccountLast4 || '',
    dialogStep,
    amountInMinorUnits,
    onAmountChange: setAmountInMinorUnits,
    onContinueToMethod,
    onContinueToAmount,
    onBackToAmount,
    selectedMethod,
    onMethodChange: setSelectedMethod,
    isPayoutAllowed,
  };
};
